<script setup lang="ts">
  import { inject } from 'vue';
  import type { Ref } from 'vue';

  import type { CustomerSegmentWithWorkflows } from '@stuut/api-sdk/models';
  import type { Partner } from '@stuut/api/models';
  import {
    PropertyList,
    PropertyListItem,
    PropertyListItemLabel,
    PropertyListItemValue,
  } from '@ui/components/property-list';

  import IntegrationLabel from '@/components/integrations/IntegrationLabel.vue';
  import { useBusinessUnits } from '@/composables/useBusinessUnits';

  interface Props {
    partner: Partner;
  }

  defineProps<Props>();

  const customerSegment = inject<Ref<CustomerSegmentWithWorkflows | undefined>>('customerSegment');
  const { hasBusinessUnits, getNameById } = useBusinessUnits();
</script>

<template>
  <div class="@container/partner_identifying_info flex min-w-0 flex-col gap-3">
    <PropertyList width="full" class="identifying-info-list min-w-0 w-full text-xs">
      <PropertyListItem v-if="partner.account_number">
        <PropertyListItemLabel>Account #</PropertyListItemLabel>
        <PropertyListItemValue>
          <span class="break-all">
            {{ partner.account_number }}
          </span>
        </PropertyListItemValue>
      </PropertyListItem>

      <PropertyListItem v-if="hasBusinessUnits && partner.business_unit_id">
        <PropertyListItemLabel>Business Unit</PropertyListItemLabel>
        <PropertyListItemValue>
          <span class="break-words">{{ getNameById(partner.business_unit_id) }}</span>
        </PropertyListItemValue>
      </PropertyListItem>

      <PropertyListItem>
        <PropertyListItemLabel>Source</PropertyListItemLabel>
        <PropertyListItemValue>
          <IntegrationLabel
            :integration="partner.linked_integration.integration"
            :last-synced-at="partner.last_synced_at?.local().format('L LT') ?? undefined"
            small
            no-icon
          />
        </PropertyListItemValue>
      </PropertyListItem>

      <PropertyListItem>
        <PropertyListItemLabel>External ID</PropertyListItemLabel>
        <PropertyListItemValue>
          <span class="break-all font-muted-background">
            {{ partner.remote_id }}
          </span>
        </PropertyListItemValue>
      </PropertyListItem>

      <PropertyListItem v-if="partner.description">
        <PropertyListItemLabel>Description</PropertyListItemLabel>
        <PropertyListItemValue>
          <span class="break-words whitespace-normal">
            {{ partner.description }}
          </span>
        </PropertyListItemValue>
      </PropertyListItem>

      <PropertyListItem v-if="partner.on_behalf_of">
        <PropertyListItemLabel>On Behalf Of</PropertyListItemLabel>
        <PropertyListItemValue>
          <span class="break-all">
            {{ partner.on_behalf_of }}
          </span>
        </PropertyListItemValue>
      </PropertyListItem>

      <PropertyListItem v-if="customerSegment">
        <PropertyListItemLabel>Segment</PropertyListItemLabel>
        <PropertyListItemValue>
          <RouterLink
            class="text-blue"
            :to="{
              name: 'playbook.collection.segmentation',
              query: { segmentation_id: customerSegment.id },
            }"
          >
            {{ customerSegment.name }}
          </RouterLink>
        </PropertyListItemValue>
      </PropertyListItem>

      <PropertyListItem v-for="(field, key) in partner.custom_fields" :key="key">
        <PropertyListItemLabel>{{ field.label }}</PropertyListItemLabel>
        <PropertyListItemValue>
          <span class="break-words">
            {{ Array.isArray(field.value) ? field.value.join(', ') : field.value }}
          </span>
        </PropertyListItemValue>
      </PropertyListItem>
    </PropertyList>
  </div>
</template>

<style scoped lang="postcss">
  @reference "../../globals.css";

  .property-list-item {
    @apply min-w-0 grid-cols-1! gap-y-1 @xs/partner_identifying_info:grid-cols-[120px_minmax(0,1fr)]! @xs/partner_identifying_info:items-start @xs/partner_identifying_info:gap-x-4 @xs/partner_identifying_info:gap-y-0;
  }

  .identifying-info-list :deep(.property-list-item > div:first-child) {
    @apply whitespace-normal @xs/partner_identifying_info:text-nowrap;
  }

  .identifying-info-list :deep(.property-list-item > div:last-child) {
    @apply min-w-0 overflow-visible whitespace-normal;
  }
</style>
